// Shared "pick an existing household, or add a new one in-flow" logic for
// create modals that need to land a record on a household (New Opportunity,
// New Transaction). Extracted from NewOpportunityModal (2026-08-06 —
// Brandon's Rosemary Springer feedback) so New Transaction can offer the
// same "Can't find them? + Add a new household" path without a second copy
// of the typeahead + duplicate-precheck logic to keep in sync.
//
// This hook owns state and side effects only. Rendering lives in
// HouseholdPickerField.tsx so the JSX itself isn't duplicated either.

import { useEffect, useState } from "react";

import { api, type DuplicatePrecheckMatch, type HouseholdListItem } from "../api";
import { isStrongDuplicateMatch } from "@shared/contacts/duplicate-match";

export interface HouseholdPicked {
  id: string;
  display_name: string;
}

export function useHouseholdPicker() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<HouseholdListItem[]>([]);
  const [picked, setPicked] = useState<HouseholdPicked | null>(null);
  const [addingHousehold, setAddingHousehold] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  // Live duplicate-precheck against the new-household fields, plus a
  // one-time confirmation the operator can give if a strong match is a
  // false positive (two different people, same name + phone). Reset
  // whenever the name/email/phone actually changes so a stale
  // confirmation can't silently ride along onto different data.
  const [dupMatches, setDupMatches] = useState<DuplicatePrecheckMatch[]>([]);
  const [confirmedNewPerson, setConfirmedNewPerson] = useState(false);

  /** Reset everything — call from the modal's "reset on open" effect. */
  function reset(initial?: HouseholdPicked | null) {
    setQuery("");
    setResults([]);
    setPicked(initial ?? null);
    setAddingHousehold(false);
    setNewFirstName("");
    setNewLastName("");
    setNewEmail("");
    setNewPhone("");
    setDupMatches([]);
    setConfirmedNewPerson(false);
  }

  /** Called after the DuplicateContactWarning's "Use this household". */
  function useExistingHousehold(householdId: string, displayName: string) {
    setPicked({ id: householdId, display_name: displayName });
    setAddingHousehold(false);
    setNewFirstName("");
    setNewLastName("");
    setNewEmail("");
    setNewPhone("");
    setDupMatches([]);
    setConfirmedNewPerson(false);
  }

  /** Called after DuplicateContactWarning's "No, this is someone new". */
  function confirmDifferentPerson() {
    setConfirmedNewPerson(true);
    setDupMatches([]);
  }

  function cancelAddingHousehold() {
    setAddingHousehold(false);
    setNewFirstName("");
    setNewLastName("");
    setNewEmail("");
    setNewPhone("");
    setDupMatches([]);
    setConfirmedNewPerson(false);
  }

  // Debounced household typeahead. Skips once a household is picked or
  // the operator has switched to the new-household form.
  useEffect(() => {
    if (picked || addingHousehold || query.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      void (async () => {
        try {
          const resp = await api.listHouseholds({ q: query.trim(), limit: 8 });
          if (!cancelled) setResults(resp.households);
        } catch {
          if (!cancelled) setResults([]);
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, picked, addingHousehold]);

  // Debounced duplicate precheck against the new-household fields — same
  // signals (email / phone / name+ZIP, though ZIP isn't collected here)
  // the server's create-time check uses, so a match surfaces before submit
  // rather than as a rejection after it.
  useEffect(() => {
    setConfirmedNewPerson(false);
    if (!addingHousehold) {
      setDupMatches([]);
      return;
    }
    const hasSignal =
      newFirstName.trim().length > 0 ||
      newLastName.trim().length > 0 ||
      newEmail.trim().length > 0 ||
      newPhone.trim().length > 0;
    if (!hasSignal) {
      setDupMatches([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      void (async () => {
        try {
          const result = await api.duplicatePrecheck({
            email: newEmail.trim() || null,
            phone: newPhone.trim() || null,
            first_name: newFirstName.trim() || null,
            last_name: newLastName.trim() || null,
          });
          if (!cancelled) setDupMatches(result.matches);
        } catch {
          // Best-effort — don't block the form if the precheck fails.
          if (!cancelled) setDupMatches([]);
        }
      })();
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [addingHousehold, newFirstName, newLastName, newEmail, newPhone]);

  const newHouseholdHasName = newFirstName.trim().length > 0 || newLastName.trim().length > 0;
  const ready = addingHousehold ? newHouseholdHasName : Boolean(picked);
  // Block submit on a strong duplicate (same name + email/phone as an
  // existing contact) until the operator either uses the matched
  // household or explicitly confirms this is a different person —
  // mirrors the server-side refusal so the button never invites a
  // submit that's just going to 409.
  const blockedByDuplicate =
    addingHousehold &&
    !confirmedNewPerson &&
    dupMatches.some((m) =>
      isStrongDuplicateMatch({ legal_first_name: newFirstName, legal_last_name: newLastName }, m),
    );

  /**
   * Resolve to a household id at submit time. When the operator is adding
   * a new household this actually creates the household + first contact
   * (POST /api/contacts); when they picked an existing one it's a no-op.
   * Throws ApiError on a server-side duplicate_contact 409 — the caller
   * should catch it and feed the matches back via reportServerDuplicate.
   */
  async function resolveHouseholdId(): Promise<{
    householdId: string;
    createdHousehold: boolean;
  } | null> {
    if (addingHousehold) {
      const created = await api.createContact({
        legal_first_name: newFirstName.trim() || null,
        legal_last_name: newLastName.trim() || null,
        email: newEmail.trim() || null,
        phone_primary: newPhone.trim() || null,
        allow_duplicate: confirmedNewPerson,
      });
      return { householdId: created.household_id, createdHousehold: true };
    }
    if (picked) return { householdId: picked.id, createdHousehold: false };
    return null;
  }

  /** The debounced precheck missed it (or the operator submitted before it
   *  ran) and the server caught it instead — surface the same match panel. */
  function reportServerDuplicate(matches: DuplicatePrecheckMatch[]) {
    setDupMatches(matches);
  }

  return {
    query,
    setQuery,
    results,
    picked,
    setPicked,
    addingHousehold,
    setAddingHousehold,
    newFirstName,
    setNewFirstName,
    newLastName,
    setNewLastName,
    newEmail,
    setNewEmail,
    newPhone,
    setNewPhone,
    dupMatches,
    confirmedNewPerson,
    setConfirmedNewPerson,
    ready,
    blockedByDuplicate,
    reset,
    useExistingHousehold,
    confirmDifferentPerson,
    cancelAddingHousehold,
    resolveHouseholdId,
    reportServerDuplicate,
  };
}

export type HouseholdPickerController = ReturnType<typeof useHouseholdPicker>;
